import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getOrderStatus } from "../../store/order";
import { getStatus } from "../../store/order/selectors";

function OrderStatusChecker({ orderNumberUuid, userEmail }) {
  const dispatch = useDispatch();
  const orderStatus = useSelector(getStatus());
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);

  const formatDateTime = (date) => {
    const dateStr = date.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const timeStr = date.toLocaleTimeString("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    return { date: dateStr, time: timeStr };
  };

  const handleCheckStatus = async () => {
    setIsCheckingStatus(true);
    try {
      await dispatch(getOrderStatus({ orderNumberUuid }));
      setLastCheckedAt(new Date());
    } finally {
      setIsCheckingStatus(false);
    }
  };

  return (
    <div id="payment-status-check" className="flex w-full flex-col items-center">
      <button
        id="payment-status-check-button"
        className="mx-2 mt-2 flex w-full justify-center rounded-md bg-blue-800 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm transition duration-300 hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-60 disabled:hover:bg-blue-800"
        onClick={handleCheckStatus}
        disabled={isCheckingStatus}
      >
        {isCheckingStatus ? "Проверка..." : "Проверить статус оплаты"}
      </button>

      {orderStatus && orderStatus.paymentStatus === "PAID" && (
        <div className="mt-4 rounded-lg border border-green-500 bg-green-50 p-4 dark:bg-green-900/20">
          <h3 className="mb-1 text-lg font-bold text-green-800 dark:text-green-300">
            ✅ Заказ оплачен!
          </h3>
          {lastCheckedAt && (
            <div className="mb-2 text-xs text-gray-500 dark:text-gray-400">
              <strong>Последняя проверка:</strong>
              <br />
              {formatDateTime(lastCheckedAt).date} {formatDateTime(lastCheckedAt).time}
            </div>
          )}
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <p className="mb-2">
              {orderStatus.orderNumber}
            </p>
            {orderStatus.codes && Object.keys(orderStatus.codes).length > 0 && (
              <div className="mt-3">
                <p className="mb-2 font-semibold">Содержимое заказа:</p>
                {Object.entries(orderStatus.codes).map(([nominal, codesArray]) => (
                  <div key={nominal} className="mb-3">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Номинал {nominal}:
                    </p>
                    <ul className="list-inside list-disc space-y-1 ml-4">
                      {codesArray.map((code, index) => (
                        <li key={index} className="font-mono text-xs">
                          {code}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-3 text-xs text-gray-600 dark:text-gray-400">
              Коды также отправлены на вашу почту: {userEmail}
            </p>
          </div>
        </div>
      )}

      {orderStatus && orderStatus.paymentStatus === "WAITING" && (
        <div className="mt-4 rounded-lg border border-[#C9D7E6] bg-[#EEF3F8] p-4 text-[#2F3B4A] dark:border-[#2B3B4B] dark:bg-[#16202A] dark:text-[#C7D3E0]">
          <h3 className="mb-1 text-lg font-bold text-[#2F3B4A] dark:text-[#C7D3E0]">
            ⏳ Заказ ожидает оплаты
          </h3>
          {lastCheckedAt && (
            <div className="mb-2 text-xs text-gray-500 dark:text-gray-400">
              <strong>Последняя проверка:</strong>
              <br />
              {formatDateTime(lastCheckedAt).date} {formatDateTime(lastCheckedAt).time}
            </div>
          )}
          <p className="text-sm text-[#2F3B4A] dark:text-[#C7D3E0]">
            Заказ ещё не оплачен или платеж пока не обработан.<br />
            Пожалуйста, подождите немного и проверьте статус снова.
          </p>
          {orderStatus.orderNumber && (
            <p className="mt-2 text-xs text-[#556579] dark:text-[#9FB0C3]">
              {orderStatus.orderNumber}
            </p>
          )}
        </div>
      )}

      {orderStatus && orderStatus.paymentStatus === "EXPIRED" && (
        <div className="mt-4 rounded-lg border border-red-500 bg-red-50 p-4 dark:bg-red-900/20">
          <h3 className="mb-1 text-lg font-bold text-red-800 dark:text-red-300">
            ❌ Заказ не был оплачен
          </h3>
          {lastCheckedAt && (
            <div className="mb-2 text-xs text-gray-500 dark:text-gray-400">
              <strong>Последняя проверка:</strong>
              <br />
              {formatDateTime(lastCheckedAt).date} {formatDateTime(lastCheckedAt).time}
            </div>
          )}
          <p className="text-sm text-red-800 dark:text-red-300">
            Попробуйте создать заказ снова или свяжитесь с <a href="https://t.me/tp_trendy_eng_shop" className="text-blue-700 dark:text-indigo-400" target="_blank" rel="noreferrer">поддержкой</a>.
          </p>
        </div>
      )}

      {orderStatus && !["PAID", "WAITING", "EXPIRED"].includes(orderStatus.paymentStatus) && (
        <div className="mt-4 rounded-lg border border-gray-500 bg-gray-50 p-4 dark:bg-gray-900/20">
          <h3 className="mb-1 text-lg font-bold text-gray-800 dark:text-gray-300">
            ❓ Неизвестный статус заказа
          </h3>
          {lastCheckedAt && (
            <div className="mb-2 text-xs text-gray-500 dark:text-gray-400">
              <strong>Последняя проверка:</strong>
              <br />
              {formatDateTime(lastCheckedAt).date} {formatDateTime(lastCheckedAt).time}
            </div>
          )}
          <p className="text-sm text-gray-800 dark:text-gray-300">
            Заказ имеет неизвестный статус. Пожалуйста, подождите немного и проверьте статус снова.
          </p>
        </div>
      )}
    </div>
  );
}

export default OrderStatusChecker;
